import { TranslationKey } from '../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../types/translation/Translator';
import LoadingPackage from '../../pages/Common/LoadingPackage';
import { handleElementKeyEvent, handleKeyEvent } from '../../../util/events.util';
import ArrowLeftIcon from '../../../VentoryUI/icons/Arrow/ArrowLeftIcon';
import ArrowRightIcon from '../../../VentoryUI/icons/Arrow/ArrowRightIcon';
import SortIcon from '../../../VentoryUI/icons/Sort/SortIcon';
import { VentoryColor } from '../../../VentoryUI/util/color.util';
import { Button } from '../../../VentoryUI/components/common/Button/Button';
import './Table.scss';
import { TestIdIdentifier, testIds } from '../../../util/identifiers/identifiers.util';
import { LoadingSpinner } from '../../pages/Common/LoadingSpinner';
import { SetUtils } from '../../../util/set/set.util';
import { Checkbox } from '../Checkbox';
import { NoItemsFound } from '../NoItems';
import { BaseFilter } from '../../../VentoryUI/components/filters/common/BaseFilter';

const HEADER_HEIGHT = 43;
const ROW_HEIGHT = 39;
const DEFAULT_COLUMN_WIDTH = 1;

function calculateColumnWidth(width: number | undefined, widthSum: number) {
  return ((width || DEFAULT_COLUMN_WIDTH) / widthSum) * 100;
}

function calculateMaxTableHeight(tableHeight: number) {
  return tableHeight - HEADER_HEIGHT;
}

function calculatePagination(maxTableHeight: number) {
  return Math.floor(maxTableHeight / ROW_HEIGHT);
}

function calculateMaxPageIndex(itemCount: number, pagination: number) {
  if (itemCount === 0) return 0;
  return Math.ceil(itemCount / (pagination || 1) - 1);
}

export interface TableHeader<T> {
  text: (item: T) => React.ReactNode;
  name: string;
  key: string;
  column?: string;
  weight?: number;
  sortValue?: (item: T) => any;
  position?: 'start' | 'end';
}

type UniqueTableIdentifier<T> = 'id' | keyof T | ((item: T) => string);

interface TableProps<T> {
  title?: string;
  modal?: boolean;
  headers: TableHeader<T>[];
  items: T[];
  loading?: boolean;
  customPagination?: number;
  selectedValues?: Set<string>;
  onSelected?: (selected: Set<string>) => void;
  onClick?: (item: T, index: number) => void;

  disabled?: (item: T) => boolean;
  disabledOnClick?: (item: T) => void;
  disabledCheckbox?: (item: T) => boolean;

  uniqueIdentifier?: UniqueTableIdentifier<T>;
  testId?: TestIdIdentifier;
  totalItemCount?: number;
  filter?: BaseFilter<any>;
}

export default function Table<T>({
  title,
  modal,
  items,
  headers,
  loading = false,
  customPagination,
  selectedValues,
  onSelected,
  onClick,
  disabledCheckbox,
  disabled,
  disabledOnClick,
  uniqueIdentifier = 'id',
  testId,
  totalItemCount,
  filter,
}: TableProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pagination, setPagination] = useState<number>(customPagination || 0);
  const [sortKey, setSortKey] = useState<string>(''); // Header key
  const [sortDescending, setSortDescending] = useState(true);

  const [selected, setSelected] = useState(new Set<string>());
  const [shiftPressed, setShiftPressed] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const [rendered, setRendered] = useState(false);

  const updatePage = (newPagination: number) => {
    const max = calculateMaxPageIndex(items.length, newPagination);
    if (items.length && (pageIndex + 1) * newPagination >= items.length) {
      setPageIndex(max);
    }
  };

  const [tableHeight, setTableHeight] = useState(0);

  const updatePagination = () => {
    if (!customPagination && ref.current) {
      const tableHeight = calculateMaxTableHeight(ref.current.offsetHeight);
      setTableHeight(tableHeight);
      const newPagination = calculatePagination(tableHeight);
      setPagination(newPagination);
      updatePage(newPagination);
    }
  };

  window.addEventListener('keydown', event =>
    handleKeyEvent(event, 'Shift', event => {
      setShiftPressed(true);
    }),
  );

  window.addEventListener('keyup', event =>
    handleKeyEvent(event, 'Shift', event => {
      setShiftPressed(false);
    }),
  );

  useEffect(() => {
    window.addEventListener('resize', updatePagination);
    window.addEventListener('orientationchange', updatePagination);
  }, [updatePagination]);

  useEffect(() => {
    if (!rendered) setRendered(true);
  }, [pagination]);

  useEffect(() => {
    updatePagination();
  }, [ref, ref.current, items]);

  useEffect(() => {
    if (onSelected) onSelected(selected);
  }, [selected]);

  useEffect(() => {
    if (!filter) return;
    const props = filter.getFilterProps();
    if (props.pageIndex) setPageIndex(props.pageIndex);
    if (props.sortBy) {
      const direction = props.sortDirection || 'asc';
      setSortKey(props.sortBy);
      setSortDescending(direction === 'desc');
    }
  }, []);

  useEffect(() => {
    if (!filter) return;

    const props = filter.getFilterProps();
    props.pageIndex = pageIndex;
    props.sortBy = sortKey;
    props.sortDirection = sortDescending ? 'desc' : 'asc';
    filter.saveFilterProps(props);
  }, [pageIndex, sortKey, sortDescending]);

  const uniqueIdentifierFromItem = (item: T): any => {
    return handleUniqueIdentifier(item, uniqueIdentifier);
  };

  const handleUniqueIdentifier = (item: T, identifier: UniqueTableIdentifier<T>) => {
    return typeof identifier === 'function' ? identifier(item) : (item as any)[identifier];
  };

  useEffect(() => {
    if (selectedValues && selected.size && !SetUtils.compare(selectedValues, selected)) setSelected(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    const itemIdentifiers = new Set(items.map(item => uniqueIdentifierFromItem(item)));
    for (const identifier of selected) {
      if (!itemIdentifiers.has(identifier)) selected.delete(identifier);
    }
    setSelected(new Set(selected));
  }, [items]);

  useEffect(() => {
    setLastSelectedIndex(-1);
  }, [pageIndex]);

  const handleHeaderClick = (header: TableHeader<T>) => {
    if (header.key === sortKey) {
      setSortDescending(!sortDescending);
    } else {
      setSortKey(header.key);
      setSortDescending(true);
    }
  };

  const handleSelectAll = () => {
    let enabledItems = sortedItems;
    if (disabledCheckbox) enabledItems = sortedItems.filter(i => !disabledCheckbox(i));

    if (selected.size === enabledItems.length && enabledItems.length) setSelected(new Set());
    else {
      const items = new Set(enabledItems.map(item => uniqueIdentifierFromItem(item)));
      setSelected(items);
    }
    setLastSelectedIndex(-1);
  };

  const handleItemSelected = (item: T, checked: boolean, rowIndex: number) => {
    if (shiftPressed && lastSelectedIndex > -1) {
      const items =
        rowIndex > lastSelectedIndex
          ? shownItems.slice(lastSelectedIndex, rowIndex + 1)
          : shownItems.slice(rowIndex, lastSelectedIndex + 1);
      if (disabledCheckbox) items.filter(i => !disabledCheckbox(i));
      const identifiers = items.map(item => uniqueIdentifierFromItem(item));
      if (checked) identifiers.forEach(i => selected.add(i));
      else identifiers.forEach(i => selected.delete(i));
    } else {
      const itemIdentifier = uniqueIdentifierFromItem(item);
      if (checked) selected.add(itemIdentifier);
      else selected.delete(itemIdentifier);
    }
    setSelected(new Set(selected));
    setLastSelectedIndex(rowIndex);
  };

  const maxPageIndex = useMemo(() => {
    return calculateMaxPageIndex(items.length, pagination);
  }, [items, pagination]);

  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const sortHeader = headers.find(h => h.key === sortKey);
    if (!sortHeader) return items;

    const sortValueFn = (item: T) => {
      if (sortHeader.sortValue) return sortHeader.sortValue(item);

      const itemValue = (item as any)[sortHeader.key];
      if (itemValue !== undefined) return itemValue;

      return sortHeader.text(item) || '';
    };

    const isEmptySortValue = (value: any) => {
      if (value === undefined || value === null) return true;
      if (typeof value === 'string') return value.trim().length === 0;
      if (Array.isArray(value)) return value.length === 0;
      return false;
    };

    return items.sort((a, b) => {
      const valueA = sortValueFn(a);
      const valueB = sortValueFn(b);
      const aEmpty = isEmptySortValue(valueA);
      const bEmpty = isEmptySortValue(valueB);

      // Keep empty values at the bottom, independent of sort direction.
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (aEmpty && bEmpty) return 0;

      if (valueA === valueB) return 0;
      if (valueA > valueB) return sortDescending ? -1 : 1;
      return sortDescending ? 1 : -1;
    });
  }, [items, sortKey, sortDescending]);

  const widthSum = useMemo(() => headers.map(h => h.weight || DEFAULT_COLUMN_WIDTH).reduce((a, b) => a + b), [headers]);

  const shownItems = sortedItems.slice(pageIndex * pagination, pageIndex * pagination + pagination);

  const content = () => {
    if (!items.length && loading) {
      return (
        <div className='flex h-full w-full items-center justify-center'>
          <LoadingPackage />
        </div>
      );
    }

    if (!items.length) {
      return (
        <div
          className='flex h-full w-full flex-col items-center justify-center rounded-2 border border-gray-default py-8'
          data-testid={testIds.noItemsFound}
        >
          <NoItemsFound />
        </div>
      );
    }

    return (
      <div className='w-full rounded-2 border border-gray-default'>
        <table className='w-full' style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {onSelected ? <col style={{ width: '52px' }} /> : null}

            {headers.map((h, index) => (
              <col key={index} style={{ width: `${calculateColumnWidth(h.weight, widthSum)}%` }} />
            ))}
          </colgroup>

          <thead>
            <tr className='rounded-t-2 bg-gray-subtle'>
              {onSelected ? (
                <th className='whitespace-nowrap pr-3'>
                  <Checkbox
                    onChange={handleSelectAll}
                    value={selected.size === sortedItems.length && !!sortedItems.length}
                  />
                </th>
              ) : null}
              {headers.map((header, index) => (
                <TH
                  key={index}
                  header={header}
                  onClick={handleHeaderClick}
                  sortKey={sortKey}
                  sortDescending={sortDescending}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {shownItems.map((item, index) => (
              <TableRow
                key={index}
                rowIndex={index}
                item={item}
                headers={headers}
                checkboxes={onSelected !== undefined}
                onClick={onClick ? item => onClick(item, index) : undefined}
                onSelect={handleItemSelected}
                selected={selected.has(uniqueIdentifierFromItem(item))}
                pageIndex={pageIndex}
                disabledCheckbox={disabledCheckbox ? disabledCheckbox(item) : false}
                disabled={disabled ? disabled(item) : false}
                disabledOnClick={disabledOnClick}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className='flex h-full flex-col bg-white'>
      {title ? (
        <p className={`font-medium ${modal ? 'mb-1' : 'mb-2'} font-semi-bold select-none text-sm text-gray-900`}>
          {title}
        </p>
      ) : undefined}

      <div ref={ref} className={`flex-grow`} style={{ height: tableHeight || undefined }}>
        {content()}
      </div>
      {items.length ? (
        <TablePagination
          totalItemCount={totalItemCount}
          shownItemCount={sortedItems.length}
          loading={loading}
          pageIndex={pageIndex}
          maxPageIndex={maxPageIndex}
          setPageIndex={setPageIndex}
          show={rendered}
        />
      ) : !customPagination && !modal ? (
        <div className='h-[34px]'></div>
      ) : null}
    </div>
  );
}

interface THProps<T> {
  header: TableHeader<T>;
  onClick: (header: TableHeader<T>) => void;
  sortKey: string;
  sortDescending: boolean;
}

function TH<T>({ header, onClick, sortKey, sortDescending }: THProps<T>) {
  const sorted = sortKey === header.key;
  const fontWeight = sorted ? 'font-bold' : 'font-semibold';

  return (
    <th className=''>
      <div
        className={`flex h-[40px] cursor-pointer select-none items-center text-xs text-gray-900 ${fontWeight}`}
        onClick={() => onClick(header)}
        style={header.position && { justifyContent: `flex-${header.position}` }}
      >
        <span className='overflow-hidden text-ellipsis text-nowrap' title={header.name}>
          {header.name}
        </span>
        {sorted ? <SortIcon sortDescending={sortDescending} className={'pl-1'} /> : null}
      </div>
    </th>
  );
}

interface TableRowProps<T> {
  item: T;
  headers: TableHeader<T>[];
  checkboxes: boolean;
  onSelect: (item: T, checked: boolean, rowIndex: number) => void;

  onClick?: (item: T) => void;
  disabledOnClick?: (item: T) => void;

  disabled?: boolean;
  disabledCheckbox?: boolean;

  selected: boolean;
  rowIndex: number;
  pageIndex: number;
}

function TableRow<T>({
  item,
  headers,
  onSelect,
  checkboxes,
  onClick,
  selected,
  rowIndex,
  disabledCheckbox,
  disabled,
  disabledOnClick,
  pageIndex,
}: TableRowProps<T>) {
  return (
    <tr
      className={`border-t hover:bg-gray-default ${onClick ? 'cursor-pointer' : '!cursor-default'} ${
        disabled ? 'bg-gray-default' : ''
      }`}
      onClick={
        disabled
          ? disabledOnClick
            ? () => disabledOnClick(item)
            : undefined
          : onClick
          ? () => onClick(item)
          : undefined
      }
      onKeyDown={onClick ? event => handleElementKeyEvent(event, ['Enter', ' '], () => onClick(item)) : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {checkboxes ? (
        <td onClick={event => event.stopPropagation()}>
          <Checkbox
            onChange={checked => onSelect(item, checked, rowIndex)}
            value={selected}
            disabled={disabledCheckbox}
          />
        </td>
      ) : null}
      {headers.map((header, index, arr) => (
        <TableCell
          key={index * (pageIndex + 1)}
          item={item}
          header={header}
          rowIndex={rowIndex}
          index={index}
          total={arr.length}
        />
      ))}
    </tr>
  );
}

interface TableCellProps<T> {
  item: T;
  header: TableHeader<T>;
  rowIndex: number;
  index: number;
  total: number;
}

function TableCell<T>({ item, header, rowIndex, index, total }: TableCellProps<T>) {
  const content = header.text(item);

  return (
    <td data-testheader={header.key} data-testindex={rowIndex}>
      <div
        className={`text-gray-90 flex h-[36px] items-center text-xs ${index !== total - 1 ? 'mr-2' : ''}`}
        style={header.position ? { justifyContent: `flex-${header.position}` } : undefined}
      >
        {typeof content === 'string' ? (
          <span
            className='overflow-hidden text-ellipsis text-nowrap'
            title={typeof content === 'string' ? content : ''}
          >
            {content}
          </span>
        ) : (
          content
        )}
      </div>
    </td>
  );
}

interface TablePaginationProps {
  totalItemCount?: number;
  shownItemCount: number;
  pageIndex: number;
  setPageIndex: (pageIndex: number) => void;
  maxPageIndex: number;
  loading: boolean;
  show: boolean;
}

function TablePagination({
  totalItemCount,
  shownItemCount,
  pageIndex,
  maxPageIndex,
  setPageIndex,
  loading,
  show,
}: TablePaginationProps) {
  const handlePrevious = () => {
    if (pageIndex > 0) setPageIndex(pageIndex - 1);
  };

  const handleNext = () => {
    if (pageIndex >= maxPageIndex) return;
    setPageIndex(pageIndex + 1);
  };

  const { first, middle, end } = useMemo(() => {
    const pageIndexes = Array.from({ length: maxPageIndex + 1 }, (_, i) => i);

    const first = pageIndexes.slice(0, Math.min(3, maxPageIndex));
    const firstSet = new Set(first);
    const end = pageIndexes.slice(pageIndexes.length - 3).filter(i => !firstSet.has(i));
    const endSet = new Set(end);
    const middle = pageIndexes.slice(pageIndex - 1, pageIndex + 2).filter(i => !firstSet.has(i) && !endSet.has(i));

    return { first, middle, end };
  }, [pageIndex, maxPageIndex]);

  return (
    <Grid
      container
      alignItems={'center'}
      justifyContent={'space-between'}
      className={`select-none py-2 text-[14px] text-gray-900 ${show ? 'visible' : 'invisible'}`}
    >
      <Grid item className='min-w-[120px]'>
        <Grid container>
          <Grid item>
            <p className='h-[26px] rounded-3 bg-gray-100 px-[8px] py-[2px]'>
              {totalItemCount !== undefined
                ? `${t(TranslationKey.showing).singular.label}: ${shownItemCount}/${totalItemCount}`
                : `${t(TranslationKey.total).singular.label}: ${shownItemCount} units`}
            </p>
          </Grid>
          <Grid item className='pl-2' marginY={'auto'}>
            {loading ? <LoadingSpinner style='primary' /> : null}
          </Grid>
        </Grid>
      </Grid>

      <Grid item>
        <Grid container>
          {first.map(i => (
            <TableNavigationItem key={i} page={i + 1} pageIndex={pageIndex} setPageIndex={setPageIndex} />
          ))}

          {first[first.length - 1] + 1 < middle[0] ? (
            <Grid item className='px-2 py-1'>
              ...
            </Grid>
          ) : null}

          {middle.length ? (
            middle.map(i => (
              <TableNavigationItem key={i} page={i + 1} pageIndex={pageIndex} setPageIndex={setPageIndex} />
            ))
          ) : first[first.length - 1] + 1 < end[0] ? (
            <Grid item className='px-2 py-1'>
              ...
            </Grid>
          ) : null}

          {middle[middle.length - 1] + 1 < end[0] ? (
            <Grid item className='px-2 py-1'>
              ...
            </Grid>
          ) : null}

          {end.map(i => (
            <TableNavigationItem key={i} page={i + 1} pageIndex={pageIndex} setPageIndex={setPageIndex} />
          ))}
        </Grid>
      </Grid>

      <Grid item>
        <Grid container columnSpacing={1}>
          <Grid item visibility={pageIndex === 0 ? 'hidden' : 'visible'}>
            <Button
              onClick={handlePrevious}
              color={{ textColor: VentoryColor.grey500, border: true }}
              startIcon={<ArrowLeftIcon />}
              disabled={pageIndex === 0}
            />
          </Grid>
          <Grid item visibility={pageIndex === maxPageIndex ? 'hidden' : 'visible'}>
            <Button
              onClick={handleNext}
              color={{ textColor: VentoryColor.grey500, border: true }}
              style='primary'
              endIcon={<ArrowRightIcon />}
              disabled={pageIndex === maxPageIndex}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

interface TableNavigationItemProps {
  page: number;
  pageIndex: number;
  setPageIndex: (pageIndex: number) => void;
}

function TableNavigationItem({ page, pageIndex, setPageIndex }: TableNavigationItemProps) {
  const selectedClass = useMemo(() => {
    const selected = pageIndex + 1 === page;
    if (selected) return 'bg-primary text-on-primary';
    return '';
  }, [pageIndex, page]);

  return (
    <Grid
      item
      className={`flex cursor-pointer items-center justify-center rounded-3 px-2 py-1 text-sm ${selectedClass}`}
      onClick={() => setPageIndex(page - 1)}
    >
      {page}
    </Grid>
  );
}
